import { motion } from 'framer-motion';
import { AccreditationBadges } from './AccreditationBadges';
import { SectionShell } from './SectionShell';

/**
 * Security & compliance — built for regulated workflows and auditability.
 *
 * Two-column block: Enterprise controls / Regulatory posture. Each side
 * is a checklist with a brand-coloured tick. A footer row lists the
 * certifications and industry bodies the platform aligns to — text-only,
 * no third-party logos to avoid endorsement implications (per §2.2 and
 * the explicit instruction to drop firm logos on the public surface).
 */

const ENTERPRISE: string[] = [
  'Encrypted document storage + access controls',
  'Retention rules by case type',
  'Versioning: every doc, every edit',
  'Immutable audit trail for reviews',
  'ISO 27001 and Cyber Essentials Plus readiness, with UK GDPR controls',
  'ISO/IEC 42001 AI management system readiness',
];

const REGULATORY: string[] = [
  'System assists; IP remains decision owner',
  'Audit logs show: who / what / when / where',
  'Evidence packs exportable for court / regulator',
  'AI drafts are clearly labelled and user controlled',
  'The system is an advisor, not a decision maker',
];

export function SecurityCompliance() {
  return (
    <SectionShell
      id="security"
      index={8}
      density="tight"
      eyebrow="Security & compliance"
      title={
        <>
          Built for{' '}
          <span className="text-brand-bright">regulated workflows</span>
        </>
      }
      intro="Asset Hound is the audit grade case file. Every action is recorded; every AI output is approved before it leaves the system"
    >
      <div className="pitch-strip grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle lg:grid-cols-2">
        <ChecklistColumn
          eyebrow="Enterprise controls"
          items={ENTERPRISE}
          accent="brand"
        />
        <ChecklistColumn
          eyebrow="Regulatory posture"
          items={REGULATORY}
          accent="brand"
        />
      </div>

      <AccreditationBadges />
    </SectionShell>
  );
}

function ChecklistColumn({
  eyebrow,
  items,
}: {
  eyebrow: string;
  items: string[];
  accent: 'brand';
}) {
  return (
    <div className="pitch-cell flex flex-col gap-5 p-7 sm:p-9">
      <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
        {eyebrow}
      </span>
      <ul className="flex flex-col gap-3">
        {items.map((item, i) => (
          <motion.li
            key={item}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.1 + i * 0.06,
            }}
            className="flex items-start gap-3 text-[15px] leading-relaxed text-ink-strong"
          >
            <CheckIcon />
            <span>{item}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

function CheckIcon() {
  return (
    <span
      aria-hidden
      className="mt-[3px] flex h-5 w-5 flex-none items-center justify-center rounded-full border border-accent-blue-border bg-accent-blue-bg"
    >
      <svg
        viewBox="0 0 14 14"
        fill="none"
        className="h-3 w-3 text-brand-bright"
        strokeWidth="2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 7.5L6 10.5L11 4.5" />
      </svg>
    </span>
  );
}
